/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
/* eslint-disable react/jsx-props-no-spreading */

import React, {
    useState, useRef, useEffect, useCallback, useReducer,
} from 'react';
import { Button, useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    object, array, any, bool, oneOfType, number, func,
} from 'prop-types';
import { uid } from 'react-uid';

const useStyles = makeStyles(({ palette, spacing, breakpoints }) => ({
    buttonContainer: {
        margin: '0px auto',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        flexFlow: 'row nowrap',
        maxWidth: `${breakpoints.values.lg}px`,
        background: palette?.background?.paper || '#fff',
        width: '100%',
        '@media (max-width: 1024px)': {
            boxShadow: `1px 1px 3px -1px ${palette.black}`,
        },
        [breakpoints.down('sm')]: {
            flexDirection: 'column-reverse',
        },
    },
    scrollButtonsContainer: {
        [breakpoints.down('sm')]: {
            overflowX: 'scroll',
        },
    },
    buttonInnerContainer: ({ hideMenu }) => ({
        width: 'fit-content',
        minWidth: '100%',
        margin: 'auto',
        display: hideMenu ? 'none' : 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'center',
        zIndex: 1,
        [breakpoints.down('sm')]: {
            flexFlow: 'row nowrap',
        },
    }),
    sticky: {
        // I want to use sticky, but overflow: hidden is set in the root component
        position: 'fixed',
        top: '0',
        left: '2%',
        right: '2%',
        width: '96%',
        boxShadow: `1px 1px 3px -1px ${palette.black}`,
        '& > button': {
            margin: `${spacing(2)}px ${spacing(3)}px`,
        },
        [breakpoints.down('sm')]: {
            left: '0',
            right: '0',
            width: '100%',
        },
    },
    childrenStickyOn: ({ buttonContainerHeight }) => ({
        paddingTop: `${buttonContainerHeight}px`,
    }),
    innerContainerWithIcon: {
        padding: '5px',
        boxSizing: 'border-box',
    },
    topIconContainer: ({ hideMenu }) => ({
        position: hideMenu ? 'relative' : 'fixed',
        top: '0',
        left: '0',
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        padding: spacing(1),
        boxSizing: 'border-box',
        [breakpoints.down('sm')]: {
            position: 'relative',
            '& button': {
                padding: 0,
                marginTop: '5px',
            },
        },
    }),
    button: {
        transition: 'background-color 0.5s, color 0.75s',
        margin: spacing(3),
        borderRadius: '20px',
        minWidth: '150px',
        whiteSpace: 'nowrap',
        [breakpoints.down('md')]: {
            margin: `${spacing(1)}px ${spacing(2)}px ${spacing(2)}px`,
        },
        [breakpoints.down('xs')]: {
            fontSize: 14,
            maxHeight: '30px',
            minWidth: 'fit-content',
            margin: `${spacing(1)}px ${spacing(1)}px ${spacing(2)}px`,
            width: 'fit-content',
            '& > span': {
                width: 'fit-content',
            },
        },
    },
    childContainer: {
        position: 'relative',
        '& > *': {
            // Needs be be relative so we can have the first child of the children be absolute
            position: 'relative',
        },
    },
    childScrollTarget: ({ buttonContainerHeight }) => ({
        height: '1px',
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        transform: `translateY(-${buttonContainerHeight}px)`,
    }),
}));

// A constant that defines the number of ms between processing an event.
const ScrollProcessingInterval = 200;

/**
 *
 * @description A wrapper that that displays buttons above children that allows automatic scrolling to each child.
 *          Each child must have a data-name prop in order to generate the title of each button.
 *          If utilizing custom child components, you must use React.forwardRef so this component can pass refs to each child.
 *          As you scroll, the button container will become sticky to the top of the screen once you scroll to it's height.
 *          As you scroll over children, the appropriate button will highlight as an indicator.
 *          If you click on a button, the screen will scroll to the corresponding child.
 * @param {Component} Container is the container component that will be used to contain the children
 * @param {object?} containerProps is an object that will be spread as props to the Container
 * @param {boolean?} hideMenu is a boolean used to control if the button menu should be displayed
 * @param {boolean || number?} forcedIndex is a forced-scroll index. If false (default), will ignore, but if changes, will force scroll
 *          to that index of child. Used for guided process.
 * @param {element?} scrollElement An optional scroll element. This is used in order to utilize this component
 *          within a dialog.
 * @param {function?} renderCloseIcon A function that returns a close icon in the correct position. Defaults to nothing.
 * @param {boolean?} showCloseIcon A boolean used to determine if the close Icon should be rendered
 */
const CYOGroupsContainer = ({
    children,
    Container,
    containerProps,
    hideMenu,
    forcedIndex,
    scrollElement,
    renderCloseIcon,
    showCloseIcon,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [focusIndexTimeout, setFocusIndexTimeout] = useState(null);
    // Used to determine which button to highlight as well as horizontal scrolling on mobile
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [addedScrollListener, setAddedScrollListener] = useState(false);
    const [buttonContainerYOffset, setButtonContainerYOffset] = useState();
    const [buttonContainerHeight, setButtonContainerHeight] = useState(0);
    const [flattenedChildren, setFlattenedChildren] = useState([]);
    // If a band is present, then this will be 1, making the first button active while over bands
    const [lowestButtonIndex, setLowestButtonIndex] = useState(0);
    // Because when we use setRefs, we have no access to the refs that have already been set. The reducer allows us to maintain state
    const [refs, setRefs] = useReducer((state, action) => (state[action.key] ? state : [...state, action.ref]), []);
    const buttonContainerRef = useRef();
    const scrollContainerRef = useRef();
    const classes = useStyles({ buttonContainerHeight, hideMenu, showCloseIcon });

    const handleButtonSelect = useCallback((index) => {
        const ref = refs[index];
        if (typeof window !== 'undefined' && ref) {
            if (isMobile && refs.length > 2) {
                // Setting focus index causes horizontal scroll on mobile. This interrupts vertical scroll. To avoid this, we use a timeout for horizontal scroll
                window.clearTimeout(focusIndexTimeout);
                setFocusIndexTimeout(window.setTimeout(() => setFocusedIndex(index), 750));
            } else {
                setFocusedIndex(index);
            }
            // Set the scroll interval time so we do not try to set the focusIndex based on scrolling
            window.sessionStorage.setItem('CYOGroupIntervalTime', Date.now() + 1500);
            // eslint-disable-next-line no-unused-expressions
            ref?.children[0]?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [refs, isMobile, focusIndexTimeout]);

    // Used to get the height of the button container and the Y offset
    // Uses previous offsets to determine if we should update
    /**
     * @description Used to set the button container offset and height.
     *      Will be used intially a few times to account for content drift.
     *      Will also be used when toggling hideMenu in order to account for changes in height
     * @param {number} offset The previous offset of the button container. Function
     *      will use the greatest value to update.
     *      If it is 0, then nothing will happen to the offset
     * @param {Array} timeouts An array of timeout numbers, in ms, to fire in a recursive manner.
     *      This will allow this function to keep the most updated offset
     */
    const setButtonContainerOffsetAndHeight = useCallback((offset, timeouts = []) => {
        if (buttonContainerRef?.current !== null && typeof window !== 'undefined') {
            let newOffset = buttonContainerRef?.current?.offsetTop || 0;
            if (newOffset < offset) {
                newOffset = offset;
            }
            setButtonContainerYOffset(newOffset);

            // Need to get computed style for the margin if any
            let buttonStyle;
            try {
                buttonStyle = window?.getComputedStyle
                    ? getComputedStyle(buttonContainerRef.current, null)
                    : { marginTop: 0, marginBottom: 0 };
            } catch {
                buttonStyle =  { marginTop: 0, marginBottom: 0 };
            }
            const marginTop = Number.isNaN(parseInt(buttonStyle.marginTop, 10)) ? 0 : parseInt(buttonStyle.marginTop, 10);
            const marginBottom = Number.isNaN(parseInt(buttonStyle.marginBottom, 10)) ? 0 : parseInt(buttonStyle.marginBottom, 10);

            setButtonContainerHeight(buttonContainerRef.current.getBoundingClientRect().height + marginTop + marginBottom);

            // If timeouts exist, set them while removing one to avoid infinite loop
            if (timeouts.length > 0) {
                setTimeout(
                    () => setButtonContainerOffsetAndHeight(newOffset, timeouts.slice(1)),
                    timeouts[0],
                );
            }
        }
    }, [buttonContainerRef]);

    // Utilizes sessionStorage to prevent processing the event every time
    const scrollListener = useCallback(() => {
        if (typeof window !== 'undefined' && scrollElement) {
            const lastProcessingTime = window.sessionStorage.getItem('CYOGroupIntervalTime');
            // Only process event if ScrollProcessInterval time has elapsed
            if (Date.now() > parseInt(lastProcessingTime, 10) + ScrollProcessingInterval || !lastProcessingTime) {
                // Check for changing active group index
                let activeElementIndex = 0;
                let fromTarget = 9999;
                for (let i = 0; i < refs.length; i += 1) {
                    if (refs[i]) {
                        const containerTopOffset = scrollElement.pageYOffset || scrollElement.scrollTop || 0;
                        const screenTarget = containerTopOffset + buttonContainerHeight;
                        const elementTarget = refs[i].offsetTop;
                        const offset = Math.abs(screenTarget - elementTarget);
                        if (offset < fromTarget) {
                            fromTarget = offset;
                            activeElementIndex = i;
                        } else {
                            break;
                        }
                    }
                }
                // The lowest we can go is the lowestButtonIndex.
                setFocusedIndex(activeElementIndex < lowestButtonIndex ? lowestButtonIndex : activeElementIndex);
                window.sessionStorage.setItem('CYOGroupIntervalTime', Date.now());
            }
        }
    }, [refs, buttonContainerYOffset, scrollElement]);

    // Adding scroll events
    useEffect(() => {
        if (scrollElement && typeof window !== 'undefined' && buttonContainerYOffset !== null && !hideMenu) {
            scrollElement.removeEventListener('scroll', scrollListener);
            scrollElement.addEventListener('scroll', scrollListener);
        }
        return () => {
            if (scrollElement && typeof window !== 'undefined') {
                scrollElement.removeEventListener('scroll', scrollListener);
            }
        };
    }, [buttonContainerYOffset, scrollListener, scrollElement, hideMenu]);

    // Get page Y offset and height of button container
    useEffect(() => {
        if (buttonContainerRef.current && buttonContainerRef.current !== null && !addedScrollListener && typeof window !== 'undefined') {
            setAddedScrollListener(true);
            // In case there is some content drift, so we will check for the button container offset a couple times
            const offset = buttonContainerRef.current.offsetTop;
            setButtonContainerOffsetAndHeight(offset, [250, 750]);
        }
    }, [buttonContainerRef, addedScrollListener, setButtonContainerOffsetAndHeight]);

    // When hideMenu changes, get the height of the container again.
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setButtonContainerOffsetAndHeight(0, [500, 1000]);
        }
    }, [hideMenu]);

    // This is used to flatten out the children, and clone them in order to pass a ref.
    // It also tracks the lowest button index that is showing (bands not included)
    useEffect(() => {
        const fChildren = [];
        const augmentedChildren = [];
        if (Array.isArray(children)) {
            children.forEach((c) => {
                if (Array.isArray(c)) {
                    c.forEach((cc) => {
                        fChildren.push(cc);
                    });
                } else {
                    fChildren.push(c);
                }
            });
        } else {
            fChildren.push(children);
        }

        fChildren.forEach((child, i) => {
            augmentedChildren.push(React.cloneElement((child), {
                ref: (ref) => { setRefs({ ref, key: i }); },
                key: child.key,
            }));
        });

        // After flattening, check for non-tracking children. Used for bands.
        for (let i = 0; i < fChildren.length; i += 1) {
            // If the child does not have a data-name, then they do not receive a button
            if (!fChildren[i].props['data-name']) {
                // Will allow correct scroll hightlighting
                setLowestButtonIndex(i + 1);
            } else {
                break;
            }
        }
        setFlattenedChildren(augmentedChildren);
    }, [children]);

    // React to parent forcing an index change
    useEffect(() => {
        if (forcedIndex !== false && typeof forcedIndex === 'number' && typeof window !== 'undefined') {
            handleButtonSelect(forcedIndex);
        }
    }, [forcedIndex]);

    // Set the initial index
    useEffect(() => {
        if (focusedIndex < lowestButtonIndex && typeof window !== 'undefined') {
            setFocusedIndex(lowestButtonIndex);
        }
    }, [lowestButtonIndex, focusedIndex]);

    // Used to scroll horizontally if active button moves out of view
    useEffect(() => {
        const buttonElem = scrollContainerRef.current?.children[0]?.children[focusedIndex - lowestButtonIndex];
        if (buttonElem && typeof window !== 'undefined' && scrollContainerRef.current?.scrollTo && isMobile) {
            const offsetLeft = buttonElem.offsetLeft;
            const scrollLeft = scrollContainerRef.current.scrollLeft;
            if ((offsetLeft < scrollLeft || offsetLeft > scrollLeft + 100)) {
                scrollContainerRef.current.scrollTo({
                    top: 0,
                    left: offsetLeft - 5,
                    behavior: 'smooth',
                });
            }
        }
    }, [focusedIndex, scrollContainerRef, isMobile, lowestButtonIndex]);

    useEffect(() => {
        if (typeof window !== 'undefined' && document && document.querySelector('#root')) {
            document.querySelector('#root').setAttribute('style', 'overflow: unset;');
        }
    }, []);

    // Once we populate all of the refs, we need to add a child to them that we will use as the scroll target for when we click a button
    useEffect(() => {
        if (refs.length === flattenedChildren.length) {
            refs.forEach((ref) => {
                // Create
                const scrollTarget = document.createElement('div');
                classes.childScrollTarget.split(' ').forEach((c) => {
                    scrollTarget.classList.add(c);
                });
                // Add scrollTarget as the first child of the child
                ref.insertBefore(scrollTarget, ref.children[0]);
            });
        }
    }, [refs]);

    return (
        <>
            <div ref={buttonContainerRef} className={classes.buttonContainer}>
                <div className={classes.topIconContainer}>
                    {renderCloseIcon()}
                </div>
                <div ref={scrollContainerRef} className={classes.scrollButtonsContainer}>
                    <div className={`${classes.buttonInnerContainer} ${showCloseIcon ? classes.innerContainerWithIcon : ''}`}>
                        {flattenedChildren.map((c, i) => c.props['data-name'] && (
                            <Button
                                onClick={() => handleButtonSelect(i)}
                                variant={i === focusedIndex ? 'contained' : 'outlined'}
                                key={uid(c.props['data-name'])}
                                color="primary"
                                className={classes.button}
                            >
                                {c.props['data-name']}
                            </Button>
                        ))}
                    </div>

                </div>

            </div>

            <Container {...containerProps} className={`${classes.childContainer} ${containerProps.className}`}>
                {flattenedChildren}
            </Container>
        </>
    );
};

CYOGroupsContainer.propTypes = {
    children: array.isRequired,
    Container: any.isRequired,
    hideMenu: bool,
    containerProps: object,
    forcedIndex: oneOfType([number, bool]),
    scrollElement: any,
    renderCloseIcon: func,
    showCloseIcon: bool,
};

CYOGroupsContainer.defaultProps = {
    containerProps: {},
    hideMenu: false,
    forcedIndex: false,
    scrollElement: false,
    renderCloseIcon: () => null,
    showCloseIcon: false,
};

export default CYOGroupsContainer;
